import {Component, OnInit, ViewChild, ViewEncapsulation} from '@angular/core';
import {RequestService} from "../../core/services/request/request.service";
import {Subscription} from "rxjs";
import {ActivatedRoute} from "@angular/router";
import {LegacyNavigateService} from "../../core/services/legacy-navigate/legacy-navigate.service";
import {
  CalculatedFieldService,
  FieldListService,
  GroupingBarService,
  IDataOptions,
  PivotChartService,
  PivotViewComponent,
  DisplayOption,
  ToolbarService,
  ToolbarItems,
  DrillThroughService,
  ConditionalFormattingService,
  NumberFormattingService
} from '@syncfusion/ej2-angular-pivotview';
import { ChartSettings } from '@syncfusion/ej2-pivotview/src/pivotview/model/chartsettings';
import {ToolbarArgs} from "@syncfusion/ej2-pivotview/src/common/base/interface";
import {InternationalizationService} from "../../core/services/internationalization/internationalization.service";

@Component({
  selector: 'sb-pivotreport-page',
  templateUrl: './pivotreport-page.component.html',
  styleUrls: ['./pivotreport-page.component.scss'],
  providers: [
    GroupingBarService,
    FieldListService,
    CalculatedFieldService,
    PivotChartService,
    ToolbarService,
    DrillThroughService,
    ConditionalFormattingService,
    NumberFormattingService
  ],
  encapsulation: ViewEncapsulation.None
})
export class PivotReportPageComponent implements OnInit {

  @ViewChild('pivotview', { static: false }) public pivotView: PivotViewComponent;

  // Exposed data

  title: string = "";

  public dataSourceSettings: IDataOptions;

  public id: string = "";

  // Pivot chart controls
  public displayOption = { view: 'Both', primary: 'Table' } as DisplayOption;
  public chartSettings = { enableMultipleAxis: true } as ChartSettings;

  // Toolbar controls
  public toolbarOptions = [
    'Grid',
    'Chart',
    'Export',
    'SubTotal',
    'GrandTotal',
    'FieldList'
  ] as ToolbarItems[];

  // Private data

  private activatedRouteSubscription: Subscription | null;

  constructor(private requestService: RequestService,
              private activatedRoute: ActivatedRoute,
              private legacyNavigateService: LegacyNavigateService,
              private i18nService: InternationalizationService) { }

  async ngOnInit() {
    // Wipe out the page context
    (window as any).KO_PAGE_CONTEXT.clear();

    // Wipe out the Knockout content.
    const pageContentMain = document.getElementById("page-content-main");
    if (pageContentMain) {
      pageContentMain.innerHTML = "";
    }
    const errorConsole = document.getElementById("error-console");
    if (errorConsole) {
      errorConsole.innerHTML = "";
    }

    // Mark as angular page
    document.getElementById("detailMain")?.classList?.add("sb-pivotreport-page-content");


    // Find the report to display.
    this.activatedRouteSubscription = this.activatedRoute.paramMap.subscribe(async params => {

      this.id = params.get("id");

      await this.loadData();
    });
  }

  beforeToolbarRender(args: ToolbarArgs) {
    args.customToolbar.push({
      prefixIcon: 'e-icons sb-e-tool-grouping',
      tooltipText: 'Grouping bar',
      click: this.groupingClicked.bind(this),
    });

    args.customToolbar.push({
      prefixIcon: 'e-icons sb-e-tool-expand', tooltipText: 'Expand/Collapse',
      click: this.expandClicked.bind(this),
    });
  }

  groupingClicked(args: any) {
    this.pivotView.showGroupingBar = !this.pivotView.showGroupingBar;
  }

  expandClicked(args: any) {
    this.pivotView.dataSourceSettings.expandAll = !this.pivotView.dataSourceSettings.expandAll;
  }

  // PivotCharts use | internally as a separator, so we have to replace it.
  private convertForbiddenChars(value: any) {
    return (value && typeof(value) === "string") ? value.replace(/\|/g, '>') : value;
  }

  private async loadData() {

    const parameters = {
      id: this.id,
    };

    const response: any = await this.requestService.createRequest()
      .controller("servicely.core.Report")
      .method("pivotReport")
      .data(parameters)
      .execute();

    const config = response.data.config;
    const dataSource: any = response.data.dataSource;

    this.title = config.Name.value;

    dataSource.dataSource.forEach((row) => {
      Object.keys(row).forEach((field) => {
        row[field] = this.convertForbiddenChars(row[field]);
      });
    });

    this.dataSourceSettings = dataSource;
  }

  // Fake button navigation

  buttonLabel(key: string): string {
    return this.i18nService.getMessage(key);
  }

  canGoBack() {
    return this.legacyNavigateService.canGoBack();
  }

  back() {
    this.legacyNavigateService.back();
  }

}
